import Phaser from 'phaser';
import type { Locomotion } from './locomotion';
import { smooth, twoBone, type Joint } from './motionPose';
import { outfitVisualProfile, weaponVisualProfile, weaponAttachmentProfile } from './appearanceProfiles';
import type { Appearance } from './character';
import { boneAttachment, HUNTER_JOINTS } from './rigAttachment';
import {ankleForSole,companionGroundY,FOOT_ORIGINS} from './actorContact';
import {itemName} from './huntEconomy';
import {followCompanion,type CompanionTrail} from './companionFollow';

const key = (part: string) => `hunter-${part}`;
let frameActorId=0;
export class HunterRig {
  readonly container: Phaser.GameObjects.Container;
  private parts = new Map<string, Phaser.GameObjects.Image>();
  private body: Phaser.GameObjects.Container;
  private weaponOverlay: Phaser.GameObjects.Graphics;
  private faceOverlay: Phaser.GameObjects.Graphics;
  private outfitOverlay: Phaser.GameObjects.Graphics;
  private weaponKind='sword';
  private outfitKind='villager';
  private preset='phu-bao-thai-ban';
  private lookKey='';
  private townFront={head:'',torso:''};
  private gender='male';
  private townMode=false;
  private face='weathered';
  private companion?:Phaser.GameObjects.Image;
  private companionLabel?:Phaser.GameObjects.Text;
  private companionShadow?:Phaser.GameObjects.Ellipse;
  private companionKind='';
  private companionTrail?:CompanionTrail;
  private lean = 0;
  private attackRemaining = 0;
  private hitRemaining = 0;
  private attackDuration = .34;
  private planted = [0,0];
  private stance = [false,false];
  private dodgeSpin = 0;
  private wasDodging = false;
  private idleSeconds=0;
  private profession='villager';
  private bamboo:Phaser.GameObjects.Graphics;
  private bridges:Phaser.GameObjects.Graphics;
  private fullFrame:Phaser.GameObjects.Image;
  private frameCache=new Map<string,Phaser.Textures.DynamicTexture>();
  private frameSerial=0;
  private readonly frameActorId=frameActorId++;
  armAngle = 0;
  kneeAngle = 0;
  constructor(scene: Phaser.Scene) {
    this.container = scene.add.container(0,0);
    this.body = scene.add.container(0,0);
    this.container.add(this.body);
    this.bridges=scene.add.graphics();this.body.add(this.bridges);
    for (const name of ['thigh-back','shin-back','foot-back','upper-arm-back','forearm-back','hand-back','pelvis','torso','head','thigh-front','shin-front','foot-front','upper-arm-front','forearm-front','hand-front','sword','charm']) {
      const part=scene.add.image(0,0,name==='charm'?'r15-equipment-talisman':key(name)).setOrigin(.5,.06);
      if(name.includes('back'))part.setTint(0xa6afa7);
      if(name==='charm')part.setVisible(false);this.parts.set(name,part);this.body.add(part);
    }
    this.outfitOverlay=scene.add.graphics();this.faceOverlay=scene.add.graphics();this.weaponOverlay=scene.add.graphics();
    this.body.add([this.outfitOverlay,this.faceOverlay,this.weaponOverlay]);
    this.bamboo=scene.add.graphics().setVisible(false);
    this.bamboo.fillStyle(0xa18a49).fillRoundedRect(-3,-20,6,27,2);
    this.bamboo.lineStyle(1,0x4e542c).lineBetween(-3,-13,3,-13).lineBetween(-3,-3,3,-3).lineBetween(-3,6,3,6);
    this.bamboo.lineStyle(2,0xc0ab70).lineBetween(0,-20,-6,-25);
    this.body.add(this.bamboo);
    this.fullFrame=scene.add.image(0,0,'__WHITE').setOrigin(.5,224/256).setVisible(false);
    this.container.add(this.fullFrame);
    this.container.setData('frame-motion',true);
    this.container.once(Phaser.GameObjects.Events.DESTROY,()=>this.clearFrames());
  }
  private clearFrames():void {
    if(this.fullFrame?.scene)this.fullFrame.setTexture('__WHITE');
    for(const texture of this.frameCache.values())texture.destroy();this.frameCache.clear();
  }
  private snapshotFrame(frame:string):void {
    let texture=this.frameCache.get(frame);
    if(!texture){
      // One image owns the entire silhouette, including gear and joint overlap.
      const key='hunter-frame-'+this.frameActorId+'-'+this.frameSerial++;
      texture=this.container.scene.textures.addDynamicTexture(key,256,256)!;
      this.body.setVisible(true);texture.draw(this.body,128,224);this.frameCache.set(frame,texture);
    }
    this.body.setVisible(false);this.fullFrame.setVisible(true).setTexture(texture.key).setAngle(0).setY(0);
    this.container.setData('motion-frame',frame);
  }
  attack(finisher=false): void { this.attackDuration=finisher?.46:.34;this.attackRemaining=this.attackDuration; }
  hit(): void { this.hitRemaining=.22; }
  get attacking(): boolean { return this.attackRemaining>0; }
  pet(kind?:'dog'|'cat'|'spirit',specimen?:string):void {
    const signature=(kind??'')+':'+(specimen??'');if(this.companionKind===signature)return;this.companionKind=signature;
    delete this.companionTrail;
    this.companion?.destroy();delete this.companion;
    this.companionLabel?.destroy();delete this.companionLabel;
    this.companionShadow?.destroy();delete this.companionShadow;
    if(kind){
      const ghost=kind==='spirit'&&specimen,key=ghost?(this.container.scene.textures.exists('market-spirit-'+specimen)?'market-spirit-'+specimen:this.container.scene.textures.exists('wr-ghost-'+specimen)?'wr-ghost-'+specimen:'gen-ghost-'+specimen):`r15-equipment-${kind}`;
        this.companionShadow=this.container.scene.add.ellipse(-48,0,ghost?35:kind==='spirit'?24:20,ghost?9:6,0x020705,.42);
        this.container.add(this.companionShadow);
        this.companion=this.container.scene.add.image(-48,0,this.container.scene.textures.exists(key)?key:'r15-equipment-spirit').setOrigin(.5,1);
      this.companion.setScale((ghost?76:kind==='spirit'?36:25)/this.companion.height);if(ghost){if(specimen==='nang-kwak'||specimen==='kuman-thong')this.companion.setAlpha(.96);else this.companion.setAlpha(.82).setTint(0xabe1d5);}this.container.add(this.companion);
      this.companion.setData('companion-body',true).setData('companion-kind',kind).setData('companion-specimen',specimen??'');
      if(ghost){this.companionLabel=this.container.scene.add.text(-48,-86,itemName('ghost-specimen-'+specimen).replace('ผีในยันต์ · ','')+(specimen==='nang-kwak'?' · DEF +3':specimen==='kuman-thong'?' · ATK +4':' · ATK +2'),{fontFamily:'Noto Sans Thai',fontSize:'9px',color:'#b6f3df',stroke:'#09100e',strokeThickness:3}).setOrigin(.5,1);this.container.add(this.companionLabel);}
    }
  }
  dress(tint:number, blade:number,weaponKind='sword',outfitKind='villager',preset='phu-bao-thai-ban',appearance?:Appearance,profession='villager',charmId=''): void {
    const signature=JSON.stringify([tint,blade,weaponKind,outfitKind,preset,appearance,profession,charmId]);if(signature===this.lookKey)return;this.lookKey=signature;this.clearFrames();
    this.profession=profession;
    this.weaponKind=weaponKind;this.outfitKind=outfitKind;this.preset=preset;
    this.face=appearance?.face??'weathered';
    const gender=appearance?.gender??(preset==='mae-kha-village'?'female':'male');
    this.gender=gender;
    const clothes:Record<string,string>={'sword-armor':'sword-outfit','spear-armor':'spear-outfit','hunter-vest':'hunter-outfit','shaman-robe':'shaman-outfit',linen:'linen'};
    for(const [name,part] of this.parts){
      const texture=name==='charm'?'r15-equipment-talisman':name==='sword'?`r15-equipment-${weaponKind}`:name==='torso'&&clothes[outfitKind]?`r15-equipment-${clothes[outfitKind]}`:`r15-${gender}-${name}`;
      if(part.scene.textures.exists(texture))part.setTexture(texture);
      part.clearTint();if(name.includes('back'))part.setTint(0xb7b5b0);
    }
    this.parts.get('charm')!.setVisible(charmId==='talisman').setOrigin(.5,0).setDisplaySize(7,11);
    this.parts.get('sword')!.setOrigin(.5,.9);
    this.townFront={head:this.parts.get('head')!.texture.key,torso:this.parts.get('torso')!.texture.key};
    const outfit=outfitVisualProfile(outfitKind,preset),weapon=weaponVisualProfile(weaponKind);
    if(outfitKind==='forest'||outfitKind==='crimson')this.parts.get('torso')!.setTint(tint);
    const skin=appearance?.skin==='deep'?0xbb9578:appearance?.skin==='light'?0xffe0c2:0xffffff;
    for(const [name,part] of this.parts)if(/head|forearm|hand|shin/.test(name))part.setTint(skin);
    this.outfitOverlay.clear();this.faceOverlay.clear();
    if(preset==='phu-bao-thai-ban')this.faceOverlay.fillStyle(0x30252a,.65).fillEllipse(8,10,7,3).fillEllipse(4,10,4,2);
    if(appearance?.hair==='tied')this.faceOverlay.fillStyle(0x2a211b,.9).fillEllipse(-9,8,7,9);
    this.weaponOverlay.clear();
    if(weapon.mode==='bow'){this.weaponOverlay.lineStyle(3,weapon.tint,1).beginPath().arc(0,0,21,-1.25,1.25).strokePath().lineStyle(1,0xe3d2aa,.9).lineBetween(7,-20,7,20);}
    if(weapon.mode==='crossbow'){this.weaponOverlay.lineStyle(3,weapon.tint,1).lineBetween(-17,0,17,0).lineBetween(0,-14,0,18).lineStyle(1,0xe3d2aa,.9).lineBetween(-16,0,0,10).lineBetween(16,0,0,10);}
    if(weapon.mode==='talisman'){this.weaponOverlay.fillStyle(0xe1c455,1).fillRect(-7,-11,14,22).lineStyle(1,0x5f3728,1).strokeRect(-7,-11,14,22).lineBetween(-4,-5,4,5).lineBetween(4,-5,-4,5);}
  }
  townFacing(north:boolean):void {
    this.townMode=true;
    for(const part of ['head','torso'] as const){const back='town-back-'+this.gender+'-'+part,front=this.townFront[part];const key=north&&(part==='head'||this.outfitKind==='indigo')&&this.container.scene.textures.exists(back)?back:front;if(key&&this.parts.get(part)!.texture.key!==key)this.parts.get(part)!.setTexture(key);}
    this.faceOverlay.setVisible(!north);
  }
  private segment(name:string,from:Joint,to:Joint,width:number): void {
    const part=this.parts.get(name)!;
    const joints=HUNTER_JOINTS[name.startsWith('upper')?'upper':name.startsWith('forearm')?'forearm':name.startsWith('thigh')?'thigh':'shin'];
    const fit=boneAttachment(part.width,part.height,joints[0],joints[1],from,to);
    part.setOrigin(joints[0].x,joints[0].y).setPosition(from.x,from.y).setScale(fit.scale).setRotation(fit.rotation);
  }
  private dead=false;
  private pose:'stand'|'sit'|'lie'='stand';
  private keepsakeId='';
  private souvenir:Phaser.GameObjects.Graphics|undefined;
  keepsake(id:string|undefined):void{
    if((id??'')===this.keepsakeId)return;this.clearFrames();this.keepsakeId=id??'';this.souvenir?.destroy();this.souvenir=undefined;if(!id)return;
    const g=this.container.scene.add.graphics(),color=id.includes('loei')?0xc44934:id.includes('nong-khai')?0x4bbaac:id.includes('uttaradit')?0xae91c8:0xd4b465;
    g.fillStyle(color,.9).fillRoundedRect(-10,1,20,7,2);g.lineStyle(1,0xe5d39c,1).strokeRoundedRect(-10,1,20,7,2);g.fillStyle(0xe8c56b,1).fillCircle(-8,8,2).fillCircle(8,8,2);this.body.add(g);this.souvenir=g;
    if(id.includes('loei')){g.fillStyle(0xa55032,1).fillTriangle(-8,2,8,2,0,-18);g.lineStyle(2,0xf0d589,1).lineBetween(-5,-4,5,-4).lineBetween(-3,-9,3,-9);g.fillStyle(0x101c19,1).fillCircle(-3,-3,1.5).fillCircle(3,-3,1.5);}
    else if(id.includes('nong-khai')){g.lineStyle(3,0x65c9b1,1).lineBetween(-6,2,-2,-8).lineBetween(-2,-8,3,-12).lineBetween(3,-12,7,-7);g.fillStyle(0xe9d78c,1).fillCircle(7,-7,2);}
    else if(id.includes('uttaradit')){g.fillStyle(0x745c88,1).fillRoundedRect(-11,-4,22,8,3);g.lineStyle(1,0xd4bf9e,1);for(let x=-8;x<=8;x+=4)g.lineBetween(x,-3,x+3,3);g.lineBetween(8,4,12,17);}
  }
  setPose(pose:'stand'|'sit'|'lie'='stand'):void {this.pose=pose;}
  setDead(dead:boolean):void {
    if(dead===this.dead)return;this.dead=dead;
    this.container.scene.tweens.killTweensOf(this.body);
    this.body.x=0;
    if(dead){this.pose='stand';this.bamboo.setVisible(false);this.attackRemaining=0;this.container.scene.tweens.add({targets:this.body,angle:88,y:-8,duration:450,ease:'Cubic.easeIn'});}
    else {this.body.setAngle(0).setY(0);this.idleSeconds=0;}
  }
  update(body: Locomotion,seconds:number,time:number,scale:number,worldX=body.x,renderedAirGap=0): void {
    const spirit=this.companionKind.startsWith('spirit:');
    if(this.companion){
      this.companionTrail=followCompanion(this.companionTrail,{ownerX:worldX,facing:body.facing,speed:Math.hypot(body.vx,body.vz),seconds});
      this.companion.x=this.companionTrail.offset*body.facing;
      this.companion.y=spirit?-10+Math.sin(time/450)*2:-Math.abs(Math.sin(time/220))*Math.min(2,Math.hypot(body.vx,body.vz)/100);
      this.companionShadow?.setPosition(this.companion.x,companionGroundY(renderedAirGap,scale)).setScale(Math.max(.45,1-renderedAirGap/250),1);
      this.companionLabel?.setPosition(this.companion.x,this.companion.y-82).setScale(body.facing,1);
      this.companion.setData('companion-follow',{offset:this.companionTrail.offset,facing:body.facing,speed:Math.hypot(body.vx,body.vz),ownerX:worldX,x:this.companion.x});
    }
    if(this.dead){this.container.setScale(scale*body.facing,scale);this.container.setData('idle-pose','dead');this.body.setVisible(false);this.fullFrame.setAngle(88).setY(-8);return;}
    this.body.setVisible(true);
    this.attackRemaining=Math.max(0,this.attackRemaining-seconds);
    this.hitRemaining=Math.max(0,this.hitRemaining-seconds);
    const speed=Math.min(1,Math.hypot(body.vx,body.vz)/260);
    const stride=Math.sin(body.gait);
    const attack=this.attackRemaining>0?Math.sin((1-this.attackRemaining/this.attackDuration)*Math.PI):0;
    const breathe=Math.sin(time/900)*.6;
    const resting=!this.townMode&&this.profession==='villager'&&this.preset==='phu-bao-thai-ban'&&this.weaponKind==='knife'&&['villager','indigo'].includes(this.outfitKind)&&body.grounded&&!body.dodging&&!body.climbing&&speed<.02&&attack===0&&this.hitRemaining===0;
    this.idleSeconds=resting?this.idleSeconds+seconds:0;
    const manualRest=this.pose!=='stand'&&speed<.02&&body.grounded&&!body.dodging&&!body.climbing&&attack===0;
    const seated=(this.idleSeconds>=3||manualRest)&&this.pose!=='lie';
    const hipY=(seated?-25:-58) + body.landingPulse*6 + (body.dodging?8:0) + (body.grounded?Math.abs(stride)*speed*1.4:0);
    this.bamboo.setVisible(resting).setPosition(15,hipY-(seated?22:3)+breathe).setAngle(seated?-8:8);
    this.container.setData('idle-pose',seated?'seated-bamboo':resting?'standing-bamboo':'active');
    if(manualRest)this.container.setData('idle-pose',this.pose);
    this.lean=smooth(this.lean,(body.vx*body.facing/260)*4+attack*12-(this.hitRemaining>0?12:0),14,seconds);
    if(body.dodging&&!this.wasDodging)this.dodgeSpin=.2;
    this.wasDodging=body.dodging;
    this.dodgeSpin=Math.max(0,this.dodgeSpin-seconds);
    this.body.rotation=body.dodging?(1-this.dodgeSpin/.2)*Math.PI*2:0;
    this.body.y=body.dodging?-48:0;
    this.body.x=0;
    if(manualRest&&this.pose==='lie'){this.body.rotation=Math.PI/2;this.body.x=-62;this.body.y=-10;}
    this.container.setScale(scale*body.facing,scale);
    const outfit=outfitVisualProfile(this.outfitKind,this.preset);
    this.bridges.clear().setY(body.dodging?48:0);
    // Continuous cloth/skin below the source cutouts closes shoulder, neck,
    // elbow, hip and knee joins even when the alpha region stops short.
    const skin=this.gender==='female'?0xb98d6d:0x9e7355;
    this.bridges.lineStyle(9,skin,1).lineBetween(0,hipY-35,3,hipY-54);
    this.bridges.fillStyle(0x313d49,1).fillEllipse(0,hipY-10,22,26);
    const pelvis=this.parts.get('pelvis')!,torso=this.parts.get('torso')!,head=this.parts.get('head')!;
    this.parts.get('charm')!.setPosition(-14,hipY-7);
    pelvis.setPosition(0,hipY-9).setScale(outfit.pelvisWidth/pelvis.width);
    torso.setPosition(-1,hipY-40+breathe).setScale(outfit.torsoHeight/torso.height).setAngle(this.lean);
    const headScale=(this.face==='angular'?19:this.preset==='phu-bao-thai-ban'?20:22)/head.width;
    const headY=hipY-38-head.height*headScale*.94+breathe;
    head.setPosition(3,headY).setScale(headScale).setAngle(-this.lean*.6);
    this.souvenir?.setPosition(3,headY+3);
    this.outfitOverlay.setPosition(-1,hipY+37+breathe).setScale(outfit.leanScale,1);this.faceOverlay.setPosition(3,headY);
    for(const [i,side] of (['front','back'] as const).entries()) {
      const wave=Math.sin(body.gait+i*Math.PI),lift=Math.max(0,Math.cos(body.gait+i*Math.PI))*speed*10;
      const hip={x:i===0?4:-5,y:hipY};
      const planted=body.grounded&&speed>.08&&Math.cos(body.gait+i*Math.PI)<0;
      if(planted&&!this.stance[i])this.planted[i]=worldX+body.facing*(wave*14)*scale;
      const stanceOffset=i===0?7:-7;
      let footX=stanceOffset+wave*14*speed,footY=-lift;
      if(planted)footX=stanceOffset+Phaser.Math.Clamp((this.planted[i]!-worldX)/(scale*body.facing),-23,23);
      if(!body.grounded){footX=i===0?11:-10;footY=body.vy>0?-16:-6;}
      if(body.climbing){footX=i===0?10:-8;footY=-15-wave*12;}
      if(seated){footX=i===0?26:12;footY=-2;}
      this.stance[i]=planted;
      const shoe=this.parts.get(`foot-${side}`)!;
      const origin=FOOT_ORIGINS[side];
      const ankleY=ankleForSole(footY,shoe.width,shoe.height,17,origin.y);
      const leg=twoBone(hip,{x:footX,y:ankleY},24,25,-1);
      this.bridges.lineStyle(8,0x35404b,1).lineBetween(hip.x,hip.y,leg.elbow.x,leg.elbow.y).lineBetween(leg.elbow.x,leg.elbow.y,leg.end.x,leg.end.y);
      this.bridges.fillStyle(0x35404b,1).fillCircle(hip.x,hip.y,6).fillCircle(leg.elbow.x,leg.elbow.y,4);
      this.segment(`thigh-${side}`,hip,leg.elbow,12);
      this.segment(`shin-${side}`,leg.elbow,leg.end,9);
      shoe.setCrop().setPosition(leg.end.x,leg.end.y).setOrigin(origin.x,origin.y).setScale(17/shoe.width).setAngle(planted?0:wave*speed*8);
      const shoulder={x:i===0?7:-7,y:hipY-32+breathe};
      let hand={x:shoulder.x-wave*10*speed+(i===0?3:-2),y:hipY+2};
      if(resting&&i===0)hand={x:15,y:hipY+1};
      if(!body.grounded)hand={x:i===0?22:-17,y:hipY-26};
      if(body.climbing)hand={x:i===0?17:4,y:hipY-54+wave*9};
      if(i===0&&attack>0)hand={x:12+attack*28,y:hipY-7-attack*34};
      if(seated&&i===0)hand={x:15,y:hipY-25+breathe};
      const arm=twoBone(shoulder,hand,18,17,i===0?-1:1);
      this.bridges.lineStyle(7,skin,1).lineBetween(shoulder.x,shoulder.y,arm.elbow.x,arm.elbow.y).lineBetween(arm.elbow.x,arm.elbow.y,arm.end.x,arm.end.y);
      this.bridges.fillStyle(skin,1).fillCircle(shoulder.x,shoulder.y,5).fillCircle(arm.elbow.x,arm.elbow.y,4);
      this.segment(`upper-arm-${side}`,shoulder,arm.elbow,10);
      this.segment(`forearm-${side}`,arm.elbow,arm.end,8);
      const handImage=this.parts.get(`hand-${side}`)!;handImage.setPosition(arm.end.x,arm.end.y-2).setScale(9/handImage.height).setAngle(i===0?-attack*50:0);
      if(i===0){
        this.armAngle=this.parts.get('forearm-front')!.angle;
        this.kneeAngle=this.parts.get('shin-front')!.angle;
        const weapon=weaponVisualProfile(this.weaponKind),blade=this.parts.get('sword')!;
        const usesSprite=true;
        blade.setVisible(usesSprite&&!resting);
        const fit=weaponAttachmentProfile(this.weaponKind);
        if(usesSprite)blade.setOrigin(fit.originX,fit.originY).setPosition(arm.end.x+2,arm.end.y+3).setDisplaySize(fit.width,fit.height).setAngle(fit.angle-attack*(weapon.mode==='bow'||weapon.mode==='crossbow'?15:130));
        this.weaponOverlay.setVisible(!usesSprite).setPosition(arm.end.x+3,arm.end.y+2).setRotation((-28-attack*130)*Math.PI/180);
      }
    }
    if(body.dodging)for(const part of this.parts.values())part.y+=48;
    const gait=Math.floor((((body.gait%(Math.PI*2))+Math.PI*2)%(Math.PI*2))/(Math.PI*2)*8);
    const frame=body.dodging?'dodge'+Math.floor(this.dodgeSpin/.04):attack>0?'attack'+Math.floor((1-this.attackRemaining/this.attackDuration)*6):this.hitRemaining>0?'hurt':manualRest?'rest-'+this.pose:seated?'seated':body.climbing?'climb'+gait:!body.grounded?'air'+(body.vy>0?'up':'down'):speed>.08?'walk'+gait:'idle'+Math.floor(time/180)%4;
    this.snapshotFrame(frame+(this.townMode?':'+this.parts.get('head')!.texture.key:''));
  }
}
